import { Component, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { format, isValid, parseISO, subDays } from 'date-fns';
import {
  COFACE_FORM_NAME,
  SUMMARY_FORM_NAME,
} from 'src/app/services/form/form.factory';
import {
  CofaceClaimFormControls,
  CofaceForm,
  SummaryForm,
} from 'src/app/services/form/form.controls';
import { CofaceClaim, Summary } from 'src/app/services/form/form.resources';
import {
  DEBTOR_FORM_STEP_NAME,
  FormService,
} from 'src/app/services/form/form.service';
import { PayeeButtonComponent } from '../../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../../../../shared/payee-icon/payee-icon.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatError } from '@angular/material/input';
import { PayeeFormFieldComponent } from '../../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDatepickerComponent } from '../../../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { DecimalPipe, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-coface-form',
  templateUrl: './coface-form.component.html',
  styleUrls: ['./coface-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    PayeeIconComponent,
    MatRadioGroup,
    MatRadioButton,
    MatError,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    MatCheckbox,
    DecimalPipe,
    DatePipe,
    TranslateModule,
  ],
})
export class CofaceFormComponent {
  private formService = inject(FormService);

  private get form(): SummaryForm {
    return this.formService.form.controls[SUMMARY_FORM_NAME]!;
  }
  get summary(): Summary {
    return this.formService.forms.find(({ name }) => name === 'summary')
      ?.data as Summary;
  }

  get cofaceForm(): CofaceForm {
    return this.form.controls[COFACE_FORM_NAME]!;
  }

  get claims() {
    return this.cofaceForm.controls.claims;
  }
  get terms_accepted() {
    return this.cofaceForm.controls.terms_accepted;
  }

  get termsAcceptedAt(): string | null {
    return this.summary.client.terms_accepted_at;
  }

  getCofaceClaimControl(
    group: FormGroup<CofaceClaimFormControls>,
    field: keyof CofaceClaimFormControls,
  ) {
    return group.controls[field];
  }

  getCofaceClaim(index: number): CofaceClaim {
    return this.summary.claims[index];
  }

  getCofaceClaimMaxInvoiceDate(index: number): string {
    const rawDueDate = this.getCofaceClaim(index).due_date_at;
    const dueDate =
      rawDueDate instanceof Date ? rawDueDate : parseISO(rawDueDate);
    if (!isValid(dueDate)) {
      return '';
    }

    return format(subDays(dueDate, 1), 'yyyy-MM-dd');
  }

  claimFileIsUploaded(form: FormGroup<CofaceClaimFormControls>): boolean {
    const value = form.value;
    return !!value.file && !(value.file instanceof File);
  }

  getClaimFileStorageUrl(form: FormGroup<CofaceClaimFormControls>): string {
    const file = form.value.file;
    return file && !(file instanceof File) ? file.storage_url : '';
  }

  getClaimFileName(form: FormGroup<CofaceClaimFormControls>): string {
    const file = form.value.file;
    if (!file) {
      return '';
    }
    return file instanceof File ? file.name : file.file_name;
  }

  openLocalClaimFile(form: FormGroup<CofaceClaimFormControls>): void {
    const file = form.value.file;
    if (!(file instanceof File)) return;
    try {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error while opening file', error);
    }
  }

  async onCofaceFileSelected(
    form: FormGroup<CofaceClaimFormControls>,
    event: Event,
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    form.patchValue({
      file,
    });
    input.value = '';
  }

  goToClaimEditor(): void {
    this.formService.redirectToStep(DEBTOR_FORM_STEP_NAME);
  }
}
